import { useEffect, useState } from 'react';
import { message } from 'antd';
import { useBatchActions } from './useBatchActions';
import { useViewContext } from '../pages/Space/Context/ViewContext';

interface UseTaskSelectionProps {
  spaceId: string | undefined;
  onSuccess?: (message: string, hidePanel?: boolean) => void;
  onInfo?: (info: string) => void;
  onError?: (error: string) => void;
}

export const useTaskSelection = ({ spaceId, onSuccess, onInfo, onError }: UseTaskSelectionProps) => {
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [confirmLoadingDeleteTasks, setConfirmLoadingDeleteTasks] = useState<boolean>(false);
  const [pendingDeleteTaskIds, setPendingDeleteTaskIds] = useState<number[] | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const viewContext = useViewContext();

  useEffect(() => {
    setSelectedItems([]);
  }, [viewContext?.view]);

  const { onBatchEdit, onDeleteTasks } = useBatchActions(
    spaceId,
    (message, hidePanel) => {
      onSuccess?.(message, hidePanel);
      if (hidePanel) {
        setSelectedItems([]);
        setPendingDeleteTaskIds(null);
      }
    },
    (info) => {
      onInfo?.(info);
    },
    (error) => {
      onError?.(error);
    }
  );

  const handleDeleteSelected = () => {
    setOpenDeleteModal(true);
  };

  const deleteTaskIds = pendingDeleteTaskIds || selectedItems;

  const handleDeleteSingleTask = (taskId: number) => {
    setPendingDeleteTaskIds([taskId]);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteTasks = () => {
    if (spaceId) {
      setConfirmLoadingDeleteTasks(true);
      onDeleteTasks(deleteTaskIds);
      setConfirmLoadingDeleteTasks(false);
      setOpenDeleteModal(false);
      setPendingDeleteTaskIds(null);
    } else {
      void messageApi.error('Failed to find space.');
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteModal(false);
    setPendingDeleteTaskIds(null);
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  return {
    selectedItems,
    setSelectedItems,
    openDeleteModal,
    confirmLoadingDeleteTasks,
    deleteTaskIds,
    messageApi,
    contextHolder,
    handleDeleteSelected,
    handleDeleteSingleTask,
    handleConfirmDeleteTasks,
    handleCancelDelete,
    clearSelection,
    onBatchEdit,
  };
};
